/**
 * Phase 6: USCIS Form Pre-Population & PDF Export Panel
 *
 * Shows the pre-populated USCIS form fields mapped from the user's workflow
 * data, grouped by official form section. Provides:
 *  - Completeness progress bar
 *  - Field-by-field preview (USCIS reference | label | pre-populated value)
 *  - Missing required fields highlighted
 *  - "Export as PDF" and "Export as Text" download buttons
 *  - Links to official USCIS form instructions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE as API } from '../lib/apiBase';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.detail?.message || data?.detail || 'Request failed';
    throw Object.assign(new Error(msg), { status: res.status, data });
  }
  return data;
};

/**
 * Trigger a file download from the backend API.
 * Uses a blob approach so the auth header can be sent.
 */
const downloadFile = async (path, filename) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.detail?.message || data?.detail || 'Download failed');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Completeness Bar
// ---------------------------------------------------------------------------
const CompletenessBar = ({ completeness }) => {
  const { required_filled, required_fields, percentage } = completeness;
  const color =
    percentage >= 80
      ? 'bg-green-500'
      : percentage >= 50
      ? 'bg-yellow-500'
      : 'bg-red-500';
  const textColor =
    percentage >= 80
      ? 'text-green-700'
      : percentage >= 50
      ? 'text-yellow-700'
      : 'text-red-700';

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-gray-700">
          Required Fields Filled
        </span>
        <span className={`text-sm font-bold ${textColor}`}>
          {required_filled} / {required_fields} ({percentage}%)
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all duration-700 ${color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Missing Fields Alert
// ---------------------------------------------------------------------------
const MissingFieldsAlert = ({ missing }) => {
  const [expanded, setExpanded] = useState(false);
  if (!missing || missing.length === 0) return null;

  const shown = expanded ? missing : missing.slice(0, 3);

  return (
    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <i className="fas fa-exclamation-triangle text-red-500"></i>
        <span className="font-semibold text-red-700 text-sm">
          {missing.length} required field{missing.length !== 1 ? 's' : ''} not yet filled
        </span>
      </div>
      <ul className="space-y-1">
        {shown.map((mf, i) => (
          <li key={i} className="text-xs text-red-600 flex items-start gap-1">
            <i className="fas fa-circle text-red-400 text-xs mt-0.5 flex-shrink-0"></i>
            {mf}
          </li>
        ))}
      </ul>
      {missing.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-red-500 underline mt-2"
        >
          {expanded ? 'Show less' : `Show ${missing.length - 3} more…`}
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Form Section
// ---------------------------------------------------------------------------
const FormSection = ({ section }) => {
  const [collapsed, setCollapsed] = useState(false);
  const filledCount = section.fields.filter((f) => f.filled).length;
  const totalCount = section.fields.length;

  return (
    <div className="mb-4 border border-gray-200 rounded-xl overflow-hidden">
      {/* Section header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="font-semibold text-gray-800 text-sm">
          {section.section_title}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {filledCount}/{totalCount} filled
          </span>
          <i
            className={`fas fa-chevron-${collapsed ? 'down' : 'up'} text-gray-400 text-xs`}
          ></i>
        </div>
      </button>

      {/* Fields */}
      {!collapsed && (
        <div className="divide-y divide-gray-100">
          {section.fields.map((field) => (
            <div
              key={field.field_ref}
              className={`px-5 py-3 ${
                !field.filled && field.required
                  ? 'bg-red-50'
                  : field.filled
                  ? 'bg-white'
                  : 'bg-gray-50/50'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Status icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {field.filled ? (
                    <i className="fas fa-check-circle text-green-500 text-sm"></i>
                  ) : field.required ? (
                    <i className="fas fa-exclamation-circle text-red-400 text-sm"></i>
                  ) : (
                    <i className="fas fa-circle text-gray-300 text-sm"></i>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-400 font-mono">
                      {field.field_ref}
                    </span>
                    {field.required && (
                      <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-semibold">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5">
                    {field.label}
                  </p>

                  {field.filled ? (
                    <p
                      className="text-sm text-gray-700 mt-1 break-words"
                      style={{ maxHeight: '80px', overflow: 'hidden' }}
                      title={field.value}
                    >
                      {typeof field.value === 'string' && field.value.length > 150
                        ? field.value.substring(0, 150) + '…'
                        : field.value}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 italic mt-1">
                      {field.required
                        ? '⚠ Not provided — return to workflow to fill this field'
                        : 'Not provided (optional)'}
                    </p>
                  )}

                  {field.help_text && !field.filled && (
                    <p className="text-xs text-blue-600 mt-1 flex items-start gap-1">
                      <i className="fas fa-info-circle mt-0.5 flex-shrink-0"></i>
                      {field.help_text}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main FormExportPanel Component
// ---------------------------------------------------------------------------
const FormExportPanel = ({ pathwayId, workflowStatus, onClose }) => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingText, setExportingText] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [exportSuccess, setExportSuccess] = useState('');

  const isSubmitted = workflowStatus === 'submitted';

  const loadFormData = useCallback(async () => {
    if (!isSubmitted) {
      setLoading(false);
      return;
    }
    try {
      const data = await apiFetch(`/api/forms/${pathwayId}`);
      setFormData(data);
    } catch (err) {
      if (err.status === 403) {
        setError('You need an active entitlement for this pathway to access form pre-population.');
      } else if (err.status === 404) {
        setError(
          'No USCIS form definition found for this pathway, or the workflow has not been submitted yet.'
        );
      } else {
        setError(err.message || 'Failed to load form data.');
      }
    } finally {
      setLoading(false);
    }
  }, [pathwayId, isSubmitted]);

  useEffect(() => {
    loadFormData();
  }, [loadFormData]);

  const handleExportPdf = async () => {
    setExportingPdf(true);
    setError('');
    setExportSuccess('');
    try {
      const formNumber = formData?.form_number || 'form';
      const filename = `${formNumber.replace('-', '')}_prepopulated_${pathwayId}.pdf`;
      await downloadFile(`/api/forms/${pathwayId}/export/pdf`, filename);
      setExportSuccess(`✅ ${formNumber} PDF downloaded successfully!`);
      setTimeout(() => setExportSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'PDF export failed. Try the text export instead.');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportDocx = async () => {
    setExportingDocx(true);
    setError('');
    setExportSuccess('');
    try {
      const formNumber = formData?.form_number || 'form';
      const filename = `${formNumber.replace('-', '')}_prepopulated_${pathwayId}.docx`;
      await downloadFile(`/api/forms/${pathwayId}/export/docx`, filename);
      setExportSuccess(`✅ ${formNumber} DOCX downloaded successfully!`);
      setTimeout(() => setExportSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'DOCX export failed. Try the PDF export instead.');
    } finally {
      setExportingDocx(false);
    }
  };

  const handleExportText = async () => {
    setExportingText(true);
    setError('');
    setExportSuccess('');
    try {
      const formNumber = formData?.form_number || 'form';
      const filename = `${formNumber.replace('-', '')}_prepopulated_${pathwayId}.txt`;
      await downloadFile(`/api/forms/${pathwayId}/export/text`, filename);
      setExportSuccess(`✅ ${formNumber} text file downloaded successfully!`);
      setTimeout(() => setExportSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Text export failed.');
    } finally {
      setExportingText(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <i className="fas fa-file-pdf text-red-500"></i>
              USCIS Form Pre-Population
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {formData
                ? `${formData.form_number} — ${formData.form_title}`
                : isSubmitted
                ? 'Loading your form data…'
                : 'Submit your workflow to access form pre-population'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">

          {/* Not submitted state */}
          {!isSubmitted && (
            <div className="text-center py-12 text-gray-500">
              <i className="fas fa-lock text-4xl mb-4 block text-gray-300"></i>
              <p className="font-medium text-gray-700 mb-2">Workflow Not Submitted</p>
              <p className="text-sm">
                Complete and submit your workflow to unlock USCIS form pre-population.
              </p>
            </div>
          )}

          {/* Loading */}
          {isSubmitted && loading && (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <i className="fas fa-spinner fa-spin text-2xl mr-3"></i>
              <span>Loading form data…</span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-lg flex items-start gap-2">
              <i className="fas fa-exclamation-triangle mt-0.5 flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Success message */}
          {exportSuccess && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 text-sm p-3 rounded-lg">
              {exportSuccess}
            </div>
          )}

          {/* Form data loaded */}
          {!loading && formData && (
            <div>
              {/* Completeness bar */}
              <CompletenessBar completeness={formData.completeness} />

              {/* Missing required fields */}
              <MissingFieldsAlert missing={formData.completeness.missing_required} />

              {/* Export buttons + USCIS link */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <button
                  onClick={handleExportPdf}
                  disabled={exportingPdf || exportingText || exportingDocx}
                  className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold text-sm disabled:opacity-50 shadow-sm"
                >
                  {exportingPdf ? (
                    <><i className="fas fa-spinner fa-spin"></i> Generating PDF…</>
                  ) : (
                    <><i className="fas fa-file-pdf"></i> Export as PDF</>
                  )}
                </button>

                <button
                  onClick={handleExportDocx}
                  disabled={exportingPdf || exportingText || exportingDocx}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold text-sm disabled:opacity-50 shadow-sm"
                >
                  {exportingDocx ? (
                    <><i className="fas fa-spinner fa-spin"></i> Generating DOCX…</>
                  ) : (
                    <><i className="fas fa-file-word"></i> Export as DOCX</>
                  )}
                </button>

                <button
                  onClick={handleExportText}
                  disabled={exportingPdf || exportingText || exportingDocx}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gray-700 text-white rounded-xl hover:bg-gray-800 transition-colors font-semibold text-sm disabled:opacity-50 shadow-sm"
                >
                  {exportingText ? (
                    <><i className="fas fa-spinner fa-spin"></i> Exporting…</>
                  ) : (
                    <><i className="fas fa-file-alt"></i> Export as Text</>
                  )}
                </button>

                {formData.uscis_instructions_url && (
                  <a
                    href={formData.uscis_instructions_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 border border-blue-300 text-blue-600 rounded-xl hover:bg-blue-50 transition-colors font-semibold text-sm"
                  >
                    <i className="fas fa-external-link-alt"></i>
                    Official {formData.form_number} on USCIS.gov
                  </a>
                )}
              </div>

              {/* Disclaimer banner */}
              <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-800">
                <i className="fas fa-exclamation-triangle mr-1"></i>
                <strong>Important:</strong> These values are pre-populated from your intake data for
                reference only. You must download the official {formData.form_number} from USCIS.gov,
                carefully transfer these values into the official form, and have it reviewed by a
                licensed immigration attorney before filing. ImmigrateSmart provides legal information,
                not legal advice.
              </div>

              {/* Sections */}
              <div>
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <i className="fas fa-list-alt text-yellow-600"></i>
                  Pre-Populated Form Fields
                  <span className="ml-1 text-sm font-normal text-gray-500">
                    ({formData.completeness.filled_fields} / {formData.completeness.total_fields} filled)
                  </span>
                </h3>
                {formData.sections.map((section) => (
                  <FormSection key={section.section_id} section={section} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {formData && (
          <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex items-center justify-between">
            <p className="text-xs text-gray-400">
              Form {formData.form_number} · {formData.completeness.percentage}% required fields complete
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleExportPdf}
                disabled={exportingPdf || exportingText || exportingDocx}
                className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50"
              >
                <i className="fas fa-file-pdf mr-1"></i>PDF
              </button>
              <button
                onClick={handleExportDocx}
                disabled={exportingPdf || exportingText || exportingDocx}
                className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50"
              >
                <i className="fas fa-file-word mr-1"></i>DOCX
              </button>
              <button
                onClick={handleExportText}
                disabled={exportingPdf || exportingText || exportingDocx}
                className="text-xs px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold disabled:opacity-50"
              >
                <i className="fas fa-file-alt mr-1"></i>Text
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormExportPanel;
